import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPasswordSchema } from '@mindpeer/auth-shared';
import { AuthLayout } from '@/components/auth-layout';
import { DarkAlert, DarkField, DarkInput, DarkSubmit, FieldIcons } from '@/components/ui';
import { useZodForm } from '@/hooks/use-zod-form';
import { api } from '@/lib/api';

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);

  const form = useZodForm({
    schema: forgotPasswordSchema,
    initialValues: { email: '' },
    onSubmit: async (input) => {
      await api.forgotPassword(input);
      setSent(true);
    },
  });

  // The same confirmation regardless of whether the address exists (PRD §16).
  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        description="If an account exists for that address, a password reset link is on its way."
        footer={
          <Link to="/sign-in" className="font-medium text-brand-400 hover:text-brand-300">
            Back to sign in
          </Link>
        }
      >
        <DarkAlert tone="success">
          The link expires after a short time. If it doesn&apos;t arrive, check your spam folder.
        </DarkAlert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot your password?"
      description="Enter your email address and we'll send you a link to choose a new one."
      footer={
        <Link to="/sign-in" className="font-medium text-brand-400 hover:text-brand-300">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={form.handleSubmit} noValidate className="space-y-5">
        {form.formError && <DarkAlert tone="error">{form.formError}</DarkAlert>}

        <DarkField label="Email" htmlFor="field-email" error={form.errorFor('email')}>
          <DarkInput
            {...form.fieldProps('email')}
            onBlur={undefined}
            type="email"
            icon={FieldIcons.mail}
            placeholder="you@company.com"
            autoComplete="username"
            required
            value={form.values.email}
            onChange={(event) => form.setValue('email', event.target.value)}
          />
        </DarkField>

        <DarkSubmit loading={form.submitting}>Send reset link</DarkSubmit>
      </form>
    </AuthLayout>
  );
}
